import * as React from 'react';
import * as UI from 'core/ui';
import { Res } from 'core/api';
import { PosScreen, Header, Content, Footer, PhoneOnly, BackActionButton, Filler, AcceptActionButton, LargeDeviceOnly } from 'pos/ui';
import { Modal } from 'core/ui-next/modal';
import { VStack, Text, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zInt } from 'core/infrastructure-next';

type Props = {
	seatNumber: Nullable<number>;
	Icon: SvgIcon
} & Modal.Actions<Nullable<number>>;

const stateSchema = z.object({
	seatNumber: zInt({ nonNegative: true }).nullable()
});

type State = z.infer<typeof stateSchema>;

export default function GetSeatNumber(props: Props) {
	const { seatNumber, Icon, cancel, close } = props;

	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, IntInput } = useControlsFor<State>();

	const onAccept = async () => {
		const result = await formRef.current!.getValue();

		if (!result.ok)
			return;

		if (result.value.seatNumber === 0) {
			result.value.seatNumber = null;
		}
		close(result.value.seatNumber);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return (
		<PosScreen screenId='GET_SEAT_NUMBER' size={'small'} useKeyPad={true}>
			<Header title={Res.Web.EDIT_SEAT_NUMBER()} onClose={cancel} />
			<Content>
				<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
					<VStack gap={'4'} justifyItems={'center'} w='full'>
						<SvgIcon src={Icon} w={'12'} h={'12'} />
						<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
							<Text fontSize={'md'} fontWeight={'bold'}>
								{Res.Web.EDIT_SEAT_NUMBER()}
							</Text>
						</VStack>
					</VStack>
					<VStack w={'full'} justifyContent={'center'}>
						<Box w={'full'}>
							<Form ref={formRef} initialValue={{ seatNumber: seatNumber === null ? 0 : seatNumber }} schema={stateSchema}>
								<IntInput linkTo='seatNumber' placeholder={Res.Web.EDIT_SEAT_NUMBER()} onKeyDown={UI.whenEnterKey(onAccept)} />
							</Form>
						</Box>
					</VStack>
					<LargeDeviceOnly>
						<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
					</LargeDeviceOnly>
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
					<Filler />
					<AcceptActionButton action={onAccept} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}